/**
 * An asset's page (`/assets/:assetId`, under the Assets entry): the head card, the actions, then the tabs Overview,
 * Proof, Success cases, Materials, Related and History (the open tab in `?tab=`).
 */

import { useParams } from 'react-router-dom';
import { FileQuestion, FileText, FolderOpen, History, Network, Trophy, TrendingUp } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useAsset } from '@/api/queries';
import type { AssetOut } from '@/api/types';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';
import { Tabs } from '@/ui/Tabs';

import { CasesTab } from '../cases/CasesTab';
import { MaterialsTab } from '../materials/MaterialsTab';
import { ProofTab } from '../proof/ProofTab';
import { RefineNotice } from '../refine/RefineNotice';
import { useAssetActions } from './AssetActions';
import { AssetFacts } from './AssetFacts';
import { HistoryTab } from './HistoryTab';
import { OverviewTab } from './OverviewTab';
import { RelatedTab } from './RelatedTab';

const BACK = { to: '/assets', name: 'Assets' };

const TABS = [
  { key: 'overview', label: 'Overview', icon: <FileText aria-hidden="true" /> },
  { key: 'proof', label: 'Proof', icon: <TrendingUp aria-hidden="true" /> },
  { key: 'cases', label: 'Success cases', icon: <Trophy aria-hidden="true" /> },
  { key: 'materials', label: 'Materials', icon: <FolderOpen aria-hidden="true" /> },
  { key: 'related', label: 'Related', icon: <Network aria-hidden="true" /> },
  { key: 'history', label: 'History', icon: <History aria-hidden="true" /> },
];

function TabBody({ tab, asset }: { tab: string; asset: AssetOut }) {
  switch (tab) {
    case 'proof':
      return <ProofTab asset={asset} />;
    case 'cases':
      return <CasesTab asset={asset} />;
    case 'materials':
      return <MaterialsTab asset={asset} />;
    case 'related':
      return <RelatedTab asset={asset} />;
    case 'history':
      return <HistoryTab assetId={asset.id} />;
    default:
      return <OverviewTab asset={asset} />;
  }
}

export function AssetPage() {
  const { assetId = '' } = useParams();
  const asset = useAsset(assetId);

  if (asset.isPending) {
    return (
      <>
        <PageHead title="Asset" back={BACK} />
        <LoadingCard label="Loading the asset" />
      </>
    );
  }
  if (asset.error) {
    const missing = asset.error instanceof ApiError && asset.error.status === 404;
    return (
      <>
        <PageHead title={missing ? 'Asset not found' : 'Asset'} back={BACK} />
        {missing ? (
          <StateCard icon={<FileQuestion aria-hidden="true" />} title="This asset does not exist or is not available to you">
            <p>It may have been removed, or it is a draft you cannot see.</p>
          </StateCard>
        ) : (
          <LoadFailure error={asset.error} onRetry={() => void asset.refetch()} />
        )}
      </>
    );
  }

  return <AssetView asset={asset.data} />;
}

function AssetView({ asset: data }: { asset: AssetOut }) {
  const actions = useAssetActions(data);
  return (
    <>
      <PageHead title={data.name} subtitle={data.tagline || 'No tagline yet'} back={BACK}>
        {actions.buttons}
      </PageHead>
      {actions.notices}
      {data.my_refinement && <RefineNotice assetId={data.id} refinement={data.my_refinement} />}
      <AssetFacts asset={data} />
      <div className="card list-card asset-card">
        <Tabs label="Asset" card tabs={TABS}>
          {(tab) => <TabBody tab={tab} asset={data} />}
        </Tabs>
      </div>
    </>
  );
}
